'use client';

import { useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useGenerateFormStore, useGenerateSeason } from '@/store/generate';
import { getCountrySeasonData, CountrySeasonData, SeasonName } from '@/lib/constants/countries';

const getCurrentSeason = (
  countryData: CountrySeasonData | undefined,
  date: Date = new Date(),
): SeasonName => {
  const month = date.getMonth() + 1;

  if (!countryData) {
    if (month >= 3 && month <= 5) return 'Spring';
    if (month >= 6 && month <= 8) return 'Summer';
    if (month >= 9 && month <= 11) return 'Autumn';
    return 'Winter';
  }

  const { hemisphere, seasonStartMonths } = countryData;

  if (hemisphere === 'N' || hemisphere === 'E') {
    if (month >= seasonStartMonths.Spring && month < seasonStartMonths.Summer) return 'Spring';
    if (month >= seasonStartMonths.Summer && month < seasonStartMonths.Autumn) return 'Summer';
    if (month >= seasonStartMonths.Autumn && month < seasonStartMonths.Winter) return 'Autumn';
    return 'Winter';
  } else {
    if (month >= seasonStartMonths.Spring && month < seasonStartMonths.Summer) return 'Spring';
    if (month >= seasonStartMonths.Summer || month < seasonStartMonths.Autumn) return 'Summer';
    if (month >= seasonStartMonths.Autumn && month < seasonStartMonths.Winter) return 'Autumn';
    return 'Winter';
  }
};

export function SeasonSelect() {
  const { season, setSeason } = useGenerateSeason();
  const countryCode = useGenerateFormStore(state => state.country);

  useEffect(() => {
    if (countryCode) {
      const countryData = getCountrySeasonData(countryCode);
      const currentSeason = getCurrentSeason(countryData);
      if (currentSeason) setSeason(currentSeason);
    }
  }, [countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSeasonChange = (value: string) => {
    setSeason(value as SeasonName);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="season">Season</Label>
      <Select value={season || ''} onValueChange={handleSeasonChange}>
        <SelectTrigger id="season">
          <SelectValue placeholder="Select a season" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Spring">🌸 Spring</SelectItem>
          <SelectItem value="Summer">☀️ Summer</SelectItem>
          <SelectItem value="Autumn">🍂 Autumn</SelectItem>
          <SelectItem value="Winter">❄️ Winter</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export const getSeasonDisplay = (season: string): string => {
  switch (season) {
    case 'Spring':
      return '🌸 Spring';
    case 'Summer':
      return '☀️ Summer';
    case 'Autumn':
      return '🍂 Autumn';
    case 'Winter':
      return '❄️ Winter';
    default:
      return season;
  }
};
